import { useState, useEffect } from 'react'
import { api, ApiError } from '../lib/api'
import type { ApiKeyUsageInfo } from '../types/api'

interface ApiKeyUsageModalProps {
  clientId: string
  keyName: string
  onClose: () => void
}

interface CopyButtonProps {
  text: string
  field: string
  copiedField: string | null
  onCopy: (text: string, field: string) => void
}

const CopyButton = ({ text, field, copiedField, onCopy }: CopyButtonProps) => (
  <button
    onClick={() => onCopy(text, field)}
    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
    title="Copy to clipboard"
  >
    {copiedField === field ? (
      <svg className="h-4 w-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    ) : (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    )}
  </button>
)

export default function ApiKeyUsageModal({ clientId, keyName, onClose }: ApiKeyUsageModalProps) {
  const [usage, setUsage] = useState<ApiKeyUsageInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [copiedField, setCopiedField] = useState<string | null>(null)

  useEffect(() => {
    const loadUsage = async () => {
      try {
        setLoading(true)
        setError(null)
        const data = await api.getApiKeyUsage(clientId)
        setUsage(data)
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err.message)
        } else {
          setError('Failed to load usage information')
        }
      } finally {
        setLoading(false)
      }
    }
    loadUsage()
  }, [clientId])

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedField(field)
      setTimeout(() => setCopiedField(null), 2000)
    } catch {
      // Fallback
      const textArea = document.createElement('textarea')
      textArea.value = text
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      setCopiedField(field)
      setTimeout(() => setCopiedField(null), 2000)
    }
  }

  const generateCurlCommand = (scope?: string) => {
    if (!usage) return ''
    const effectiveScope = scope || usage.scope
    return `curl -X POST "${usage.token_endpoint}" \\
  -H "Content-Type: application/x-www-form-urlencoded" \\
  -d "grant_type=${usage.grant_type}" \\
  -d "client_id=${usage.client_id}" \\
  -d "client_secret=YOUR_CLIENT_SECRET" \\
  -d "scope=${effectiveScope}"`
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-modal w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">How to Use This API Key</h3>
              <p className="mt-1 text-sm text-gray-500">{keyName}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 overflow-y-auto flex-1">
            {loading && (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-accent border-t-transparent"></div>
                <p className="mt-2 text-gray-500">Loading usage information...</p>
              </div>
            )}

            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {usage && !loading && (
              <div className="space-y-6">
                {/* Introduction */}
                <p className="text-sm text-gray-600">
                  Use the <strong>OAuth2 client credentials flow</strong> to obtain an access token. 
                  The returned JWT access token should be included as a Bearer token in API requests.
                </p>

                {/* Configuration values */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-900">Configuration</h4>
                  
                  <div className="bg-gray-50 rounded-lg divide-y divide-gray-200">
                    {/* Token Endpoint */}
                    <div className="p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Token Endpoint</span>
                        <CopyButton copiedField={copiedField} onCopy={copyToClipboard} text={usage.token_endpoint} field="token_endpoint" />
                      </div>
                      <p className="mt-1 font-mono text-sm text-gray-900 break-all">{usage.token_endpoint}</p>
                    </div>

                    {/* Client ID */}
                    <div className="p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Client ID</span>
                        <CopyButton copiedField={copiedField} onCopy={copyToClipboard} text={usage.client_id} field="client_id" />
                      </div>
                      <p className="mt-1 font-mono text-sm text-gray-900 break-all">{usage.client_id}</p>
                    </div>

                    {/* Scopes - per target app */}
                    {usage.target_apps && usage.target_apps.length > 1 ? (
                      usage.target_apps.map((app) => (
                        <div key={app.name} className="p-3">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                              Scope — {app.display_name}
                            </span>
                            <CopyButton copiedField={copiedField} onCopy={copyToClipboard} text={app.scope} field={`scope_${app.name}`} />
                          </div>
                          <p className="mt-1 font-mono text-sm text-gray-900 break-all">{app.scope}</p>
                          <p className="mt-0.5 text-xs text-gray-500">Roles: {app.roles.join(', ')}</p>
                        </div>
                      ))
                    ) : (
                      <div className="p-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Scope</span>
                          <CopyButton copiedField={copiedField} onCopy={copyToClipboard} text={usage.scope} field="scope" />
                        </div>
                        <p className="mt-1 font-mono text-sm text-gray-900 break-all">{usage.scope}</p>
                      </div>
                    )}

                    {/* Grant Type */}
                    <div className="p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Grant Type</span>
                        <CopyButton copiedField={copiedField} onCopy={copyToClipboard} text={usage.grant_type} field="grant_type" />
                      </div>
                      <p className="mt-1 font-mono text-sm text-gray-900">{usage.grant_type}</p>
                    </div>
                  </div>
                </div>

                {/* cURL examples */}
                <div className="space-y-3">
                  {usage.target_apps && usage.target_apps.length > 1 ? (
                    <>
                      <h4 className="text-sm font-medium text-gray-900">Example Requests (cURL)</h4>
                      <p className="text-xs text-gray-500">
                        Your API key has access to multiple APIs. Use the appropriate scope for each:
                      </p>
                      {usage.target_apps.map((app) => (
                        <div key={app.name} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <h5 className="text-xs font-medium text-gray-700">{app.display_name}</h5>
                            <CopyButton copiedField={copiedField} onCopy={copyToClipboard} text={generateCurlCommand(app.scope)} field={`curl_${app.name}`} />
                          </div>
                          <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                            <pre className="text-sm text-gray-100 whitespace-pre-wrap">
                              <code>{generateCurlCommand(app.scope)}</code>
                            </pre>
                          </div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <>
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-gray-900">Example Request (cURL)</h4>
                        <CopyButton copiedField={copiedField} onCopy={copyToClipboard} text={generateCurlCommand()} field="curl" />
                      </div>
                      <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                        <pre className="text-sm text-gray-100 whitespace-pre-wrap">
                          <code>{generateCurlCommand()}</code>
                        </pre>
                      </div>
                    </>
                  )}
                  <p className="text-xs text-amber-600 flex items-start gap-2">
                    <svg className="h-4 w-4 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span>Replace <code className="bg-amber-100 px-1 rounded">YOUR_CLIENT_SECRET</code> with the actual client secret you received when the key was created.</span>
                  </p>
                </div>

                {/* Response example */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-900">Example Response</h4>
                  <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                    <pre className="text-sm text-gray-100">
                      <code>{`{
  "token_type": "Bearer",
  "expires_in": 3599,
  "access_token": "eyJ0eXAiOiJKV1QiLCJhbGciOi..."
}`}</code>
                    </pre>
                  </div>
                </div>

                {/* Using the token */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-900">Using the Token</h4>
                  <p className="text-sm text-gray-600">
                    Include the access token in the <code className="bg-gray-100 px-1 rounded">Authorization</code> header of your API requests:
                  </p>
                  <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                    <pre className="text-sm text-gray-100">
                      <code>Authorization: Bearer eyJ0eXAiOiJKV1QiLCJhbGciOi...</code>
                    </pre>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
